import {
  TextCapitalized,
  Spacer,
  TableTagTransaction,
  dateFormatterDayMonthYear,
  dateFormatterHourMinute,
} from "@qivia/ui";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppSelector } from "../../../../redux/hooks";
import { selectManager } from "../../homeSlice";
import { CategoriesMerchantCorrectionModal } from "../modals/categoriesMerchantCorrectionModal";
import { PublicTransactionsPropsType } from "../transactionsAPI";
import { DisplayReasonsRefused } from "./displayReasonsRefused";
import { SendSmsButton } from "./sendSmsButton";
import { SendSmsModal } from "./sendSmsModal";
import { SupportingDocumentModal } from "./supportingDocumentModal";
import { colors } from "@qivia/ui/src/styles/figmaColors";

const JT_FINANCES_COMPANY_GROUP = "c6d23e02-ceb8-43a2-a010-4606a3196847";

export const SidePanelHeader = (props: {
  transaction: PublicTransactionsPropsType;
  canCorrectMerchantCategory?: boolean;
  sendSmsReplaceInvalidAction: (props: {
    driverPhone: string;
    driverName: string;
    transactionUuid: string;
  }) => void;
  getSupportingDocumentAction: (supportingDocumentUuid: string) => void;
  getLastReminderDateAction: (transactionUuid: string) => void;
  sendSmsAction: (props: {
    driverPhone: string;
    driverName: string;
    transactionUuid: string;
  }) => void;
}) => {
  const [displaySendSmsModal, setDisplaySendSmsModal] =
    useState<boolean>(false);
  const [
    isMerchantAuthorizationModalVisible,
    setIsMerchantAuthorizationModalVisible,
  ] = useState<boolean>(false);
  const { t } = useTranslation();
  const manager = useAppSelector(selectManager);

  const isOwnerManager = useMemo(() => {
    if (!manager) {
      return false;
    }
    return manager.role === "OWNER";
  }, [manager]);

  const isAdminManagerExcludedJtFinancesCompany = useMemo(() => {
    if (!manager) {
      return false;
    }
    return (
      manager.role === "ADMIN" &&
      manager.companyGroup !== JT_FINANCES_COMPANY_GROUP
    );
  }, [manager]);

  const categoryCorrectionDisplayed =
    props.transaction.categoryAlreadyCorrected === false &&
    props.transaction.category === null &&
    props.canCorrectMerchantCategory === true &&
    (isOwnerManager || isAdminManagerExcludedJtFinancesCompany);

  const isRefund = props.transaction.type === "REFUND";
  const isCanceled = props.transaction.status === "CANCELED";

  return (
    <StyledPanelContainer>
      <StyledHeader>
        <TextCapitalized>
          {props.transaction.category &&
            t(`transactions.category.${props.transaction.category}`)}
        </TextCapitalized>
      </StyledHeader>
      <Spacer y={0.5} />
      <TableTagTransaction
        status={t(`transactions.status.${props.transaction.status}`)}
      />
      <Spacer y={1.5} />
      <StyledRowAmount $isRefund={isRefund}>
        <StyledAmount $isCanceled={isCanceled}>
          {isRefund ? "+ " : ""}
          {props.transaction.amount}
        </StyledAmount>
        <StyledCurrency>
          {props.transaction.amount && (
            <>
              &nbsp;EUR <Spacer y={0.25} />
            </>
          )}
        </StyledCurrency>
      </StyledRowAmount>
      <Spacer y={1} />
      <StyledBodyM>{props.transaction.merchantName}</StyledBodyM>
      <Spacer y={0.25} />
      <StyledDate>
        <div>{dateFormatterDayMonthYear(new Date(props.transaction.date))}</div>
        <div>
          {dateFormatterHourMinute(new Date(props.transaction.date)).replace(
            ":",
            "h",
          )}{" "}
        </div>
      </StyledDate>
      <Spacer y={1} />
      {props.transaction.status !== "CANCELED" && (
        <>
          {(props.transaction.status === "ACCEPTED" ||
            props.transaction.status === "PENDING") &&
            props.transaction.supportingDocumentUuid && (
              <SupportingDocumentModal
                supportingDocumentUuid={
                  props.transaction.supportingDocumentUuid
                }
                transaction={props.transaction}
                sendSmsReplaceInvalidAction={props.sendSmsReplaceInvalidAction}
                getSupportingDocumentAction={props.getSupportingDocumentAction}
              />
            )}

          {props.transaction.status === "REJECTED" &&
            props.transaction.reasonsRefused &&
            props.transaction.reasonsRefused.length > 0 && (
              <DisplayReasonsRefused
                reasonsRefused={props.transaction.reasonsRefused}
                setIsMerchantAuthorizationModalVisible={
                  setIsMerchantAuthorizationModalVisible
                }
                categoryCorrectionDisplayed={categoryCorrectionDisplayed}
              />
            )}
          {props.transaction.supportingDocumentStatus === "MISSING" &&
            props.transaction.areSupportingDocumentsMandatory &&
            (props.transaction.status === "ACCEPTED" ||
              props.transaction.status === "PENDING") &&
            props.transaction.driverPhone && (
              <SendSmsButton
                onClick={() => setDisplaySendSmsModal(true)}
                transactionUuid={props.transaction.uuid}
                getLastReminderDateAction={props.getLastReminderDateAction}
              />
            )}
          <SendSmsModal
            transaction={props.transaction}
            isModalOpen={displaySendSmsModal}
            onCloseModal={() => setDisplaySendSmsModal(false)}
            sendSmsAction={props.sendSmsAction}
          />
          <CategoriesMerchantCorrectionModal
            visible={isMerchantAuthorizationModalVisible}
            onCloseModal={() => setIsMerchantAuthorizationModalVisible(false)}
            transaction={props.transaction}
          />
        </>
      )}
    </StyledPanelContainer>
  );
};

const StyledBodyM = styled.div`
  ${typographies["Body/M"]}
`;
const StyledRowAmount = styled.div<{ $isRefund?: boolean }>`
  display: flex;
  align-items: flex-end;
  color: ${({ $isRefund }) =>
    $isRefund && colors["colors/system/success/success_normal"]};
`;

//NO TYPO
const StyledAmount = styled.div<{
  $isCanceled?: boolean;
}>`
  font-size: 2.25rem;
  font-style: normal;
  font-weight: 400;
  text-decoration: ${({ $isCanceled }) =>
    $isCanceled ? "line-through" : "none"};
`;
//NO TYPO
const StyledCurrency = styled.div`
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
`;

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledHeader = styled.div`
  ${typographies["Header/H4"]}
`;

const StyledDate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]}
`;
